import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Tooltip from "components/Tooltip";

const KPI_TOOLTIPS = {
  'Lotes Activos': {
    title: 'Lotes Activos',
    description: 'Número de lotes en estado Activo o Procesando. Son las posiciones abiertas de tu inventario.',
  },
  'Exposición Total': null,
  'Listos para Venta': {
    title: 'Listos para Venta',
    description: 'Lotes cuyo procesamiento ha finalizado y están disponibles para ser vendidos. Cuanto más tiempo pasen sin venderse, mayor es el riesgo de mercado acumulado.',
  },
  'Completados (mes)': {
    title: 'Vendidos este Mes',
    description: 'Lotes cerrados (vendidos) en el mes en curso. Indica el ritmo de rotación de tu inventario.',
  },
};

export default function OperativeSummary({ batches }) {
  const navigate = useNavigate();

  const activeBatches = batches?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status)) || [];
  const readyForSale = batches?.filter(b => b?.batch_status === 'Listo para Venta') || [];

  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const completedThisMonth = batches?.filter(b => {
    if (b?.batch_status !== 'Completado') return false;
    const updated = b?.updated_at ? new Date(b.updated_at) : null;
    return updated && updated >= thisMonthStart;
  }) || [];

  const totalExposure = activeBatches?.reduce(
    (sum, b) => sum + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0), 0
  );

  // Average inventory age
  const avgAge = useMemo(() => {
    if (activeBatches?.length === 0) return 0;
    const totalDays = activeBatches?.reduce((sum, b) => {
      const created = b?.created_at ? new Date(b.created_at) : new Date();
      return sum + Math.floor((Date.now() - created?.getTime()) / (1000 * 60 * 60 * 24));
    }, 0);
    return Math.round(totalDays / activeBatches?.length);
  }, [activeBatches]);

  // Top 3 batches by exposure
  const top3 = useMemo(() => {
    return [...activeBatches]?.map(b => ({
        ...b,
        exposure: (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0),
      }))?.sort((a, b) => b?.exposure - a?.exposure)?.slice(0, 3);
  }, [activeBatches]);

  const kpis = [
    { label: 'Lotes Activos', value: String(activeBatches?.length), icon: 'Layers', color: 'var(--color-accent)' },
    { label: 'Exposición Total', value: `${(totalExposure / 1000)?.toFixed(0)}k €`, icon: 'TrendingUp', color: 'var(--color-warning)' },
    { label: 'Listos para Venta', value: String(readyForSale?.length), icon: 'CheckCircle', color: 'var(--color-success)' },
    { label: 'Vendidos (mes)', value: String(completedThisMonth?.length), icon: 'Archive', color: 'var(--color-muted-foreground)' },
  ];

  return (
    <div className="card p-4 lg:p-5 min-w-0 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Icon name="BarChart2" size={18} color="var(--color-accent)" strokeWidth={2} />
        <Tooltip
          title="Resumen Operativo"
          description="Vista rápida del estado actual de tu operación: lotes activos, exposición, material listo para vender y resultados del mes."
        >
          <h3 className="font-heading font-semibold text-base text-foreground">Resumen Operativo</h3>
        </Tooltip>
      </div>
      {/* KPI mini-cards */}
      <div className="grid grid-cols-2 gap-2">
        {kpis?.map(item => {
          const tip = KPI_TOOLTIPS?.[item?.label];
          return (
            <div
              key={item?.label}
              className="p-3 rounded-lg border border-border flex flex-col gap-1"
              style={{ background: item?.color + '08' }}
            >
              <div className="flex items-center gap-1.5">
                <Icon name={item?.icon} size={13} color={item?.color} strokeWidth={2} />
                {tip ? (
                  <Tooltip title={tip?.title} description={tip?.description}>
                    <span className="text-xs font-caption text-muted-foreground leading-tight">{item?.label}</span>
                  </Tooltip>
                ) : (
                  <span className="text-xs font-caption text-muted-foreground leading-tight">{item?.label}</span>
                )}
              </div>
              <p className="font-data font-bold text-sm text-foreground">{item?.value}</p>
            </div>
          );
        })}
      </div>
      {/* Average inventory age */}
      <div
        className="flex items-center justify-between p-3 rounded-lg border border-border"
        style={{ background: avgAge > 45 ? 'var(--color-warning)08' : 'var(--color-muted)10' }}
      >
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={14} color={avgAge > 45 ? 'var(--color-warning)' : 'var(--color-muted-foreground)'} strokeWidth={2} />
          <Tooltip
            title="Antigüedad Media del Inventario"
            description="Promedio de días que llevan los lotes activos en inventario. Una antigüedad alta aumenta la exposición acumulada al riesgo de mercado."
          >
            <span className="text-xs font-caption text-muted-foreground">Antigüedad media del inventario</span>
          </Tooltip>
        </div>
        <span
          className="font-data font-semibold text-sm"
          style={{ color: avgAge > 45 ? 'var(--color-warning)' : 'var(--color-foreground)' }}
        >
          {avgAge} días
        </span>
      </div>
      {/* Top 3 batches by exposure */}
      <div className="flex flex-col gap-2">
        <Tooltip
          title="Mayores Posiciones"
          description="Los tres lotes con mayor exposición económica actual. Son tus posiciones más relevantes — un movimiento de mercado les afecta más que al resto."
        >
          <p className="text-xs font-caption text-muted-foreground font-medium">Top 3 lotes por exposición</p>
        </Tooltip>
        {top3?.length === 0 ? (
          <p className="text-xs font-caption text-muted-foreground italic">Sin lotes activos</p>
        ) : (
          top3?.map((b, i) => (
            <div
              key={b?.id}
              className="flex items-center justify-between gap-2 p-2.5 rounded-md border border-border"
              style={{ background: 'var(--color-muted)08' }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-data font-bold flex-shrink-0"
                  style={{ background: 'var(--color-accent)20', color: 'var(--color-accent)' }}
                >
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-caption font-medium text-foreground truncate">{b?.metal_name || '—'}</p>
                  <p className="text-xs font-caption text-muted-foreground truncate">{b?.category_name || b?.scrap_category || '—'}</p>
                </div>
              </div>
              <span className="font-data text-xs font-semibold text-foreground flex-shrink-0">
                {b?.exposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
              </span>
            </div>
          ))
        )}
        {top3?.length > 0 && (
          <button
            onClick={() => navigate('/inventory-management')}
            className="text-xs font-caption flex items-center gap-1 transition-standard hover:opacity-70 self-end"
            style={{ color: 'var(--color-accent)' }}
          >
            Ver inventario <Icon name="ArrowRight" size={12} color="var(--color-accent)" strokeWidth={2} />
          </button>
        )}
      </div>
      {/* CTA */}
      <div className="pt-1 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          iconName="Calculator"
          iconPosition="left"
          className="w-full"
          onClick={() => navigate('/batch-calculator')}
        >
          Nueva calculadora →
        </Button>
      </div>
    </div>
  );
}
